/**
 * pieceBand.js — the client's copy of the PIECE GUARD money check (spec §5.2).
 *
 * The bands here MUST match backend/services/pieceGuard.js. This is the early word
 * shown under the quantity field WHILE THE PERSON IS TYPING; the gate is the
 * enforcement. If the two drift, the form starts either nagging about piece counts
 * that will be accepted or staying silent about ones that will be rejected after the
 * bill is finished — and both of those teach the user to ignore it.
 *
 * WHY THIS FILE EARNS ITS KEEP
 *   Most mistakes die here, silently, with no popup at all. Somebody types "1", sees
 *   "₹27.50 per piece ⚠ usually ₹6.88" appear under the field, and fixes it themselves
 *   before pressing anything. Popups are only for what survives this.
 *
 *   The interruption budget is finite and non-renewable: every unnecessary popup
 *   devalues every necessary one. You buy the right to hard-block on a real error by
 *   never interrupting on a false one.
 */

// Mirrors NORMAL_BAND / PACK_RATIO_BASE / PACK_RATIO_SLOPE / PLAUSIBLE_PACKS in
// pieceGuard.js. These two files MUST agree: this one decides what the person is told
// while they type, and that one decides what happens when they press save.
const NORMAL_BAND = 0.25;
// H3 — was a flat relative 0.15, which around k=2 meant every per-piece ratio in
// [1.70, 2.30] was called a pack mistake. A doubling is routine for Indian vegetables,
// eggs and oil, so the most confident message this system can produce was being fired at
// correct entries. See pieceGuard.js for the full reasoning; the numbers are the same.
const PACK_RATIO_BASE = 0.13;
const PACK_RATIO_SLOPE = 0.06;
// F9 — the width for k >= 4, as a fraction of k. H3's absolute slope was meant for k=2/3,
// where a false accusation was the problem; applied to every k it narrowed a flat ±15% to
// about ±6% at k=12, where the risk is the opposite one — missing a real case-of-12 error.
const PACK_RATIO_RELATIVE = 0.12;
const CORROBORATION_REQUIRED_AT_OR_BELOW = 3;
const PLAUSIBLE_PACKS = [2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 24, 30, 48, 50, 100];

export const isPcsUnit = (unit) => String(unit || '').trim().toLowerCase() === 'pcs';

const money = (n) => `₹${Number(n).toFixed(2)}`;

/** Mirrors pieceGuard.packFactorFor, tolerance rule and all. */
export const packFactorFor = (ratio, profilePack) => {
  if (!Number.isFinite(ratio) || ratio <= 1) return null;

  const candidates = Number.isInteger(profilePack) && profilePack >= 2
    ? [...new Set([profilePack, ...PLAUSIBLE_PACKS])]
    : PLAUSIBLE_PACKS;

  // F9 — nearest PLAUSIBLE PACK, not nearest integer. `Math.round(ratio) === k` made
  // overlap impossible but also capped every window at ±0.5, so a case-of-12 error carried
  // in on a 10% price rise (ratio 13.2, which rounds to 13 — not a pack size at all) was
  // missed. Exactly one k can claim a ratio either way. See pieceGuard.js for the full
  // reasoning; the numbers and the shape are the same, and pieceBand.test.js sweeps the
  // whole range to prove the two files still agree.
  const nearest = candidates.reduce(
    (best, k) => (best === null || Math.abs(ratio - k) < Math.abs(ratio - best) ? k : best),
    null
  );
  if (nearest === null) return null;

  const desired = nearest <= CORROBORATION_REQUIRED_AT_OR_BELOW
    ? PACK_RATIO_BASE + PACK_RATIO_SLOPE * (nearest - 1)
    : PACK_RATIO_RELATIVE * nearest;

  // …and a window may never reach its neighbour on the side the ratio lies, so a ratio
  // sitting between two equally plausible packs belongs to neither rather than being
  // awarded to one by arithmetic accident.
  const side = candidates
    .filter(k => (ratio > nearest ? k > nearest : k < nearest))
    .reduce((best, k) => (best === null || Math.abs(k - nearest) < Math.abs(best - nearest) ? k : best), null);
  const neighbourCap = side === null ? Infinity : 0.45 * Math.abs(side - nearest);

  return Math.abs(ratio - nearest) <= Math.min(desired, neighbourCap) ? nearest : null;
};

/** Mirrors pieceGuard.overCountFactorFor — H4, the same mistake the other way round. */
export const overCountFactorFor = (ratio, profilePack) => {
  if (!Number.isFinite(ratio) || ratio <= 0 || ratio >= 1) return null;
  return packFactorFor(1 / ratio, profilePack);
};

/** Mirrors pieceGuard.corroborate — a small factor must match the item's own pack. */
export const corroborate = (factor, profilePack) => {
  if (!factor) return null;
  if (factor > CORROBORATION_REQUIRED_AT_OR_BELOW) return factor;
  return factor === Number(profilePack) ? factor : null;
};

/**
 * The live hint for one `pcs` row, or null.
 *
 * Returns null aggressively. A row with no profile, no benchmark, or nothing typed yet
 * has no "usual", and inventing one is how a warning system loses its credibility on
 * day one.
 *
 * @param {{quantity, total_price, unit}} row
 * @param {{piecesPerPack, pieceDescription, status}|null} profile
 * @param {{typicalCost, mature, standardUnit}|null} bench
 * @returns {{level:'green'|'amber'|'red', pricePerPiece:number, message:string,
 *            packFactor:number|null, proposedQuantity:number|null}|null}
 */
export const pieceBandVerdict = (row, profile, bench) => {
  if (!row || !isPcsUnit(row.unit)) return null;

  const qty = Number(row.quantity);
  const total = Number(row.total_price);
  if (!Number.isFinite(qty) || qty <= 0) return null;
  if (!Number.isFinite(total) || total <= 0) return null;

  const pricePerPiece = total / qty;

  // With no mature per-piece benchmark there is nothing to compare against. Show the
  // arithmetic anyway — "₹27.50 per piece" is itself informative, and it is the same
  // sentence the §4 read-back relies on to make a wrong entry sound absurd.
  const benchReady = bench && bench.mature && Number(bench.typicalCost) > 0 && bench.standardUnit === 'pcs';
  if (!benchReady) {
    return {
      level: 'green',
      pricePerPiece,
      packFactor: null,
      proposedQuantity: null,
      message: `${money(pricePerPiece)} per piece`,
    };
  }

  const typical = Number(bench.typicalCost);
  const deviation = Math.abs(pricePerPiece - typical) / typical;

  if (deviation <= NORMAL_BAND) {
    return {
      level: 'green',
      pricePerPiece,
      packFactor: null,
      proposedQuantity: null,
      message: `${money(pricePerPiece)} per piece ✓`,
    };
  }

  const pack = profile ? Number(profile.piecesPerPack) : null;

  /**
   * ── H8.1 — THE CLIENT MAY NOT ACCUSE WHERE THE SERVER ONLY SUGGESTS ─────────
   *
   * §8.1: a PROVISIONAL profile may suggest, never accuse. The backend obeys it — it
   * downgrades every piece verdict to a `_unproven` warning while the profile is
   * unproven — and this function was handed the profile, which carries `status`, and
   * NEVER READ IT. So the live hint showed the confident red "Did 1 packet of 4 arrive?"
   * for a definition the server would not have blocked on, with the full authority of a
   * statement of fact.
   *
   * That inverts the rule at exactly the moment it matters most. If the very first
   * definition of an item was itself the mistake, an accusing hint tells the person
   * entering it CORRECTLY that they are wrong — and the bug seals itself, defended by the
   * screen that was supposed to catch it.
   *
   * Provisional → amber and hedged. Trusted → the confident red it has earned.
   */
  const trusted = !profile || profile.status === 'trusted';

  // H8.3 — `pack` is null for a row whose item has no profile at all, and `null === 1` is
  // false, so `singlePiece` was false and the hint below happily rendered "Did 1 packet of
  // 4 arrive?" for an item the SERVER would refuse outright with piece_profile_required.
  // A missing profile is not an opinion about the pack size; it is the absence of one.
  const hasPack = Number.isInteger(pack) && pack >= 1;
  const singlePiece = pack === 1;

  const ratio = pricePerPiece / typical;
  const factor = hasPack ? corroborate(packFactorFor(ratio, pack), pack) : null;
  const overFactor = hasPack ? corroborate(overCountFactorFor(ratio, pack), pack) : null;

  // The pack-mistake case gets its own wording, because "this is 4× the usual price"
  // is a puzzle and "this looks like a packet was entered as 1 piece" is a fix.
  if (factor && hasPack && !singlePiece) {
    const proposed = qty * factor;
    return trusted
      ? {
          level: 'red',
          pricePerPiece,
          packFactor: factor,
          proposedQuantity: proposed,
          unproven: false,
          message: `${money(pricePerPiece)} per piece — usually ${money(typical)}. Did ${qty} packet${qty === 1 ? '' : 's'} of ${pack} arrive (${proposed} pieces)?`,
        }
      : {
          level: 'amber',
          pricePerPiece,
          packFactor: factor,
          proposedQuantity: proposed,
          unproven: true,
          message: `${money(pricePerPiece)} per piece — ${row.item_name || 'this item'} may come ${pack} to a packet, so this could be ${proposed} pieces. Worth a check.`,
        };
  }

  // H4 — the over-count, in the hint as well as in the gate.
  if (overFactor && hasPack && !singlePiece && qty % overFactor === 0) {
    const proposed = qty / overFactor;
    return trusted
      ? {
          level: 'red',
          pricePerPiece,
          packFactor: overFactor,
          proposedQuantity: proposed,
          unproven: false,
          message: `${money(pricePerPiece)} per piece — usually ${money(typical)}. Were these ${proposed} piece${proposed === 1 ? '' : 's'} rather than ${qty}?`,
        }
      : {
          level: 'amber',
          pricePerPiece,
          packFactor: overFactor,
          proposedQuantity: proposed,
          unproven: true,
          message: `${money(pricePerPiece)} per piece — that is well under the usual ${money(typical)}. If packets were counted as pieces this would be ${proposed}. Worth a check.`,
        };
  }

  return {
    level: 'amber',
    pricePerPiece,
    packFactor: null,
    proposedQuantity: null,
    unproven: !trusted,
    message: `${money(pricePerPiece)} per piece — you usually pay about ${money(typical)}`,
  };
};

/**
 * The quick-add chips (§5.1) — the highest-value change in the whole spec.
 *
 * The person thinks "one packet arrived", taps +4, and the field reads 4 pcs. They act
 * in their real mental model, but the field, the unit and the stored value are `pcs`
 * from start to finish. The wrong number never gets typed in the first place.
 *
 * §5.4: a declared single-piece item (coconut, cake, watermelon) gets NO chips.
 * Offering "+1 +2 +3" there is noise, and noise is what teaches people to stop reading.
 */
export const quickAddChipsFor = (profile) => {
  const pack = profile ? Number(profile.piecesPerPack) : null;
  if (!Number.isInteger(pack) || pack < 2) return [];
  return [pack, pack * 2, pack * 3];
};

/** The permanent row caption (§5.3): "1 pc = one 4-finger bar". */
export const pieceCaptionFor = (profile) => {
  if (!profile || !profile.pieceDescription) return null;
  return `1 pc = ${profile.pieceDescription}`;
};

export const PIECE_NORMAL_BAND = NORMAL_BAND;
export const PIECE_PACK_RATIO_BASE = PACK_RATIO_BASE;
export const PIECE_PACK_RATIO_SLOPE = PACK_RATIO_SLOPE;
export const PIECE_PLAUSIBLE_PACKS = PLAUSIBLE_PACKS;
